import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import SignaturePad from 'signature_pad';

@Component({
  selector: 'app-e-signature-dialog',
  templateUrl: './e-signature-dialog.component.html',
  styleUrls: ['./e-signature-dialog.component.scss']
})
export class ESignatureDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('signaturePad') signaturePadElement!: ElementRef;
  @HostListener('document:keydown.escape', ['$event'])
    onEscPressed(event: KeyboardEvent) {
      this.cancel();
    }
  
  private signaturePad!: SignaturePad;
  signatureData: any = null;
  isSignatureValid: boolean = false;
  
  // New fields for name and date
  signatureName: string = '';
  signatureDate: Date = new Date();

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) { }

  ngOnInit(): void {
    // Check if we're in edit mode and populate fields with existing data
    if (this.config.data?.isEditMode && this.config.data?.signatureToEdit) {
      const signatureToEdit = this.config.data.signatureToEdit;
      
      // Populate name field
      if (signatureToEdit.name) {
        this.signatureName = signatureToEdit.name;
      }
      
      // Populate date field
      if (signatureToEdit.date) {
        // Handle both string and Date object formats
        if (typeof signatureToEdit.date === 'string') {
          this.signatureDate = new Date(signatureToEdit.date);
        } else if (signatureToEdit.date instanceof Date) {
          this.signatureDate = signatureToEdit.date;
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.initializeSignaturePad();
  }

  private initializeSignaturePad(): void {
    const canvas = this.signaturePadElement.nativeElement;
    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)'
    });

    // Handle signature changes
    this.signaturePad.addEventListener('endStroke', () => {
      this.onSignatureChange();
    });

    // Load existing signature image if in edit mode
    if (this.config.data?.isEditMode && this.config.data?.signatureToEdit?.url) {
      this.loadExistingSignature(this.config.data.signatureToEdit.url);
    }
  }

  private loadExistingSignature(imageUrl: string): void {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = this.signaturePadElement.nativeElement;
      const ctx = canvas.getContext('2d');
      
      // Clear the canvas first
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw the existing signature image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Mark signature as valid since we loaded an existing one
      this.isSignatureValid = true;
      this.signatureData = canvas.toDataURL();
    };
    img.onerror = () => {
      console.warn('Could not load existing signature image');
    };
    img.src = imageUrl;
  }

  onSignatureChange(): void {
    if (this.signaturePad) {
      this.isSignatureValid = !this.signaturePad.isEmpty();
      if (this.isSignatureValid) {
        this.signatureData = this.signaturePad.toDataURL();
      }
    }
  }

  clearSignature(): void {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.signatureData = null;
      this.isSignatureValid = false;
    }
  }

  submitSignature(): void {
    if (this.isFormValid()) {
      // Format date to YYYY-MM-DD format using local date (no timezone conversion)
      const year = this.signatureDate.getFullYear();
      const month = String(this.signatureDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.signatureDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      // Emit signature data back to calling component
      this.ref.close({
        signature: this.signatureData,
        name: this.signatureName.trim(),
        date: formattedDate,
        success: true
      });
    }
  }

  isFormValid(): boolean {
    return this.isSignatureValid && 
           this.signatureName.trim().length > 0 && 
           this.signatureDate !== null;
  }

  cancel(): void {
    // Emit cancellation back to calling component
    this.ref.close({
      success: false
    });
  }
} 